import TableCellHeader from '@/components/Table/TableCellHeader';
import {
  CyclingIcon,
  RunningIcon,
  SearchIcon,
  SwimmingIcon,
  VirtualRideIcon,
} from '@/components/icons';
import { loadState } from '@/utils/localStorage';
import { roundNumber } from '@/utils/maths';
import { getIntensityFactor, getTSS } from '@/utils/metrics';
import { Stack, useTheme } from '@mui/material';
import clsx from 'clsx';
import { useState } from 'react';
import SimpleBar from 'simplebar-react';
import { Checkbox, NoData, Tooltip } from '../components';
import Filters from '../components/Filters';
import Head from '../components/Head';
import SkeletonLoader from '../components/SkeletonLoader';
import {
  Table as MUITable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '../components/Table';
import TextField from '../components/TextField';
import { useActivities } from '../hooks/useActivities';
import { useFilters } from '../hooks/useFilters';
import styles from '../style.module.css';
import { Activity } from '../types';
import { getIsRace, getSpeed } from '../utils/activities';
import { getSufferScoreColor } from '../utils/common';
import { getDate, getDuration } from '../utils/date';
import { UserSettings } from './Appbar';

const sxProps = {
  backgroundColor: 'yellow',
  borderRadius: '30%',
  width: '24px',
  height: '24px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: '#fff',
  fontSize: '16px',
};

const iconMap: { [key: string]: JSX.Element } = {
  Run: (
    <div style={sxProps} className={styles.run}>
      <RunningIcon />
    </div>
  ),
  Ride: (
    <div style={sxProps} className={styles.ride}>
      <CyclingIcon />
    </div>
  ),
  VirtualRide: (
    <div style={sxProps} className={styles.virtualride}>
      <VirtualRideIcon />
    </div>
  ),
  Swim: (
    <div style={sxProps} className={styles.swim}>
      <SwimmingIcon />
    </div>
  ),
};

type Order = 'asc' | 'desc';

const minCyclingRange_init = 0;
const maxCyclingRange_init = 250; // km
const minRunningRange_init = 0;
const maxRunningRange_init = 50; // km

type SortBy = keyof Activity | 'intensityFactor' | 'tss';

const Table = () => {
  const { activities, isLoading, error } = useActivities();
  const [searchString, setSearchString] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('start_date');
  const [order, setOrder] = useState<Order>('desc');
  const [showRaces, setShowRaces] = useState(false);

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value !== ' ') {
      setSearchString(value);
    }
  };

  const handleClear = () => {
    setSearchString('');
  };

  const {
    sportIncludesFilter,
    runDistanceFilter,
    rideDistanceFilter,
    handleRunningRangeChange,
    handleCyclingRangeChange,
    displayVirtualRide,
    toggleDisplayVirtualRide,
    displaySwimming,
    toggleDisplaySwimming,
    displayCycling,
    toggleDisplayCycling,
    displayRunning,
    toggleDisplayRunning,
  } = useFilters(
    minRunningRange_init,
    maxRunningRange_init,
    minCyclingRange_init,
    maxCyclingRange_init
  );

  const theme = useTheme();

  const searchFilter = (activity: Activity) =>
    activity.name.toLowerCase().includes(searchString.toLowerCase().trim());

  const racesFilter = (activity: Activity) => {
    if (!showRaces) return true;
    return activity.workout_type === 1 || activity.workout_type === 11;
  };

  const activitiesToDisplay = activities
    .filter(sportIncludesFilter)
    .filter(rideDistanceFilter)
    .filter(runDistanceFilter)
    .filter(searchFilter)
    .filter(racesFilter)
    .map((activity) => {
      const tss = getTSS(activity);
      const localStorageKey = 'userSettings';
      const storedUserSettings: UserSettings = loadState(localStorageKey);
      const FTP = storedUserSettings?.FTP;
      const intensityFactor = getIntensityFactor(activity, FTP);
      return {
        ...activity,
        tss,
        intensityFactor,
      };
    });

  const nbActivities = activitiesToDisplay.length;

  // ordre croissant
  const ascendingCompare = (a: any, b: any) => {
    // on remplace les suffer_score undefined par 0
    const A = a[sortBy] || 0;
    const B = b[sortBy] || 0;

    if (A < B) {
      return -1;
    }
    if (A > B) {
      return 1;
    }
    return 0;
  };

  activitiesToDisplay.sort((a, b) => {
    if (sortBy === 'start_date_local') return 0;
    return order === 'asc' ? ascendingCompare(a, b) : -ascendingCompare(a, b);
  });

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!activities) {
    return <NoData>No activities</NoData>;
  }

  if (error) {
    return <div>{'An error has occurred: ' + (error as any).message}</div>;
  }

  const handleClickHeader = (property: SortBy) => {
    setSortBy(property);
    const isAsc = sortBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
  };

  const handleClickRace = () => {
    setShowRaces((prevState) => !prevState);
  };

  // https://mui.com/material-ui/react-table/#virtualized-table

  return (
    <div id="table">
      <Head title="Strava Dashboard - table" />
      <div>
        <h2>{`Activities: ${nbActivities}`}</h2>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <TextField
            value={searchString}
            onChange={handleChange}
            handleClear={handleClear}
            startAdornment={<SearchIcon />}
            placeholder="Search activity"
          />
          <Filters
            displayCycling={displayCycling}
            toggleDisplayCycling={toggleDisplayCycling}
            displayRunning={displayRunning}
            toggleDisplayRunning={toggleDisplayRunning}
            displaySwimming={displaySwimming}
            toggleDisplaySwimming={toggleDisplaySwimming}
            displayVirtualRide={displayVirtualRide}
            toggleDisplayVirtualRide={toggleDisplayVirtualRide}
            minCyclingRange_init={minCyclingRange_init}
            maxCyclingRange_init={maxCyclingRange_init}
            handleCyclingRangeChange={handleCyclingRangeChange}
            minRunningRange_init={minRunningRange_init}
            maxRunningRange_init={maxRunningRange_init}
            handleRunningRangeChange={handleRunningRangeChange}
          />
          <Checkbox id="race" checked={showRaces} onChange={handleClickRace}>
            Races only 🏆
          </Checkbox>
        </Stack>
      </div>
      <SimpleBar style={{ height: 'calc(100vh - 250px)' }}>
        <TableContainer>
          <MUITable sx={{ maxWidth: 1500 }}>
            <TableHead>
              <TableRow
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <TableCellHeader
                  onClick={() => handleClickHeader('sport_type')}
                  selected={sortBy === 'sport_type'}
                  order={order}
                >
                  Sport
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('start_date')}
                  selected={sortBy === 'start_date'}
                  order={order}
                >
                  Date
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('name')}
                  selected={sortBy === 'name'}
                  order={order}
                >
                  Name
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('moving_time')}
                  selected={sortBy === 'moving_time'}
                  order={order}
                >
                  Chrono
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('distance')}
                  selected={sortBy === 'distance'}
                  order={order}
                >
                  Distance (km)
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('average_speed')}
                  selected={sortBy === 'average_speed'}
                  order={order}
                >
                  Allure
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('total_elevation_gain')}
                  selected={sortBy === 'total_elevation_gain'}
                  order={order}
                >
                  Denivele (m)
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('average_watts')}
                  selected={sortBy === 'average_watts'}
                  order={order}
                >
                  Watts
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('suffer_score')}
                  selected={sortBy === 'suffer_score'}
                  order={order}
                >
                  Effort
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('tss')}
                  selected={sortBy === 'tss'}
                  order={order}
                >
                  <Tooltip placement="top" title="Training Stress Score">
                    <span>TSS</span>
                  </Tooltip>
                </TableCellHeader>
                <TableCellHeader
                  onClick={() => handleClickHeader('intensityFactor')}
                  selected={sortBy === 'intensityFactor'}
                  order={order}
                >
                  <Tooltip placement="top" title="Intensity Factor">
                    <span>IF</span>
                  </Tooltip>
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {nbActivities < 1 ? (
                <NoData>No activities</NoData>
              ) : (
                activitiesToDisplay.map((activity) => {
                  const {
                    name,
                    id,
                    start_date,
                    moving_time,
                    distance,
                    sport_type,
                    average_speed,
                    total_elevation_gain,
                    suffer_score,
                    workout_type,
                    average_watts,
                    tss,
                    intensityFactor,
                  } = activity;
                  const speed = getSpeed(sport_type, average_speed);
                  const date = new Date(start_date);
                  const startDay = getDate.format(date);
                  const isRace = getIsRace(workout_type);
                  const { hours, minuts, seconds } = getDuration(moving_time);

                  return (
                    <TableRow
                      key={id}
                      sx={{
                        fontWeight: isRace ? 600 : 'normal',
                        background: isRace ? '#e6e6e6' : 'none',
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1}>
                          {iconMap[sport_type]}
                          {sport_type}
                          {isRace && '🏆 '}
                        </Stack>
                      </TableCell>
                      <TableCell>{startDay}</TableCell>
                      <TableCell
                        className={clsx(
                          styles.nameCell,
                          styles[`color-${sport_type.toLocaleLowerCase()}`]
                        )}
                      >
                        <a href={`https://www.strava.com/activities/${id}`}>
                          {name}
                        </a>
                      </TableCell>
                      <TableCell>{`${hours}:${minuts}:${seconds}`}</TableCell>
                      <TableCell>{roundNumber(distance / 1000)}</TableCell>
                      <TableCell>{speed}</TableCell>
                      <TableCell>{total_elevation_gain}</TableCell>
                      <TableCell>{average_watts}</TableCell>
                      <TableCell
                        style={{ color: getSufferScoreColor(suffer_score) }}
                      >
                        {suffer_score}
                      </TableCell>
                      <TableCell>{tss}</TableCell>
                      <TableCell>{intensityFactor}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </MUITable>
        </TableContainer>
      </SimpleBar>
    </div>
  );
};

export default Table;
